import dayjs, { Dayjs } from 'dayjs';

export interface IDatePeriod {
  key: string;
  dates: {
    start: Dayjs;
    startLabel: string;
    end: Dayjs;
    endLabel: string;
  };
  label: string;
}

const setToFiveAM = (date: Dayjs): Dayjs =>
  date.set('hour', 5).set('minute', 0).set('second', 0).set('millisecond', 0);

export const generateCustomPeriod = (from: Dayjs, to: Dayjs): IDatePeriod => {
  const fromFiveAM = setToFiveAM(from);
  const toFiveAM = setToFiveAM(to);

  return {
    key: 'custom',
    dates: {
      start: fromFiveAM,
      startLabel: fromFiveAM.format('YYYY-MM-DD hh:mm'),
      end: toFiveAM,
      endLabel: toFiveAM.format('YYYY-MM-DD hh:mm'),
    },
    label: 'Eget datum',
  };
};

/**
 * The custom period is meant to be a placeholder, that is used
 * as an interim value before the user has picked something in a calendar.
 * This approach is used to not have to change the IDatePeriod type so
 * dates are optional.
 */
export const generateCustomPeriodPlaceholder = (): IDatePeriod => {
  const now = dayjs();
  return {
    key: 'custom',
    dates: {
      start: now,
      startLabel: now.format('YYYY-MM-DD hh:mm'),
      end: now,
      endLabel: now.format('YYYY-MM-DD hh:mm'),
    },
    label: 'Eget datum',
  };
};

export const generateDayPeriods = (): IDatePeriod[] => {
  const rightNow = dayjs();
  const fiveAM = setToFiveAM(dayjs());
  const isBeforeFiveAM = rightNow.isBefore(fiveAM);

  let startOfToday = isBeforeFiveAM
    ? dayjs().startOf('day').subtract(1, 'day')
    : dayjs().startOf('day');
  startOfToday = setToFiveAM(startOfToday);
  const endOfToday = startOfToday.add(1, 'day');
  const startOfYesterday = startOfToday.subtract(1, 'day');
  const endOfYesterday = startOfToday;

  const dates = [
    {
      key: 'today',
      dates: {
        start: startOfToday,
        startLabel: startOfToday.format('YYYY-MM-DD hh:mm'),
        end: endOfToday,
        endLabel: endOfToday.format('YYYY-MM-DD hh:mm'),
      },
      label: 'Dagens',
    },
    {
      key: 'yesterday',
      dates: {
        start: startOfYesterday,
        startLabel: startOfYesterday.format('YYYY-MM-DD hh:mm'),
        end: endOfYesterday,
        endLabel: endOfYesterday.format('YYYY-MM-DD hh:mm'),
      },
      label: 'Gårdagens',
    },
  ];

  return dates;
};

export const generateMonthPeriods = (): IDatePeriod[] => {
  const monthNumbers = Array.from(Array(12).keys());
  const getFirstOfMonth = (delta: number) => {
    const monthsBack = dayjs().subtract(delta, 'months').set('date', 1);
    return setToFiveAM(monthsBack);
  };

  const months = monthNumbers.map((key) => {
    const start = getFirstOfMonth(key);
    const end = start.add(1, 'month');

    return {
      key: key.toString(),
      dates: {
        start,
        startLabel: start.format('YYYY-MM-DD hh:mm'),
        end,
        endLabel: end.format('YYYY-MM-DD hh:mm'),
      },
      label: start.format('MMMM YYYY'),
    };
  });

  return months;
};
